import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import QuickStats from '../../components/Dashboard/QuickStats';
import ChartCard from '../../components/Dashboard/ChartCard';
import ActivityFeed, { ActivityItem as FeedActivityItem } from '../../components/Dashboard/ActivityFeed';
import LineChart from '../../components/Charts/LineChart';
import PieChart from '../../components/Charts/PieChart';
import BarChart from '../../components/Charts/BarChart';
import { palette, withAlpha } from '../../theme/palette';
import {
  invoicesApi,
  courtCasesApi,
  tasksApi,
  extractList,
} from '../../api/facades';
import { useAuth } from '../../context/AuthContext';
import { Folder, DollarSign, CheckSquare, FileText } from '../../utils/icons';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();

  const { data: invoicesRes } = useQuery({
    queryKey: ['dashboard-invoices'],
    queryFn: () => invoicesApi.list({ perPage: 200 }),
  });
  const { data: casesRes } = useQuery({
    queryKey: ['dashboard-cases'],
    queryFn: () => courtCasesApi.list({ perPage: 200 }),
  });
  const { data: tasksRes } = useQuery({
    queryKey: ['dashboard-tasks'],
    queryFn: () => tasksApi.list({ per_page: 200 } as any),
  });

  const invoices = extractList(invoicesRes);
  const cases = extractList(casesRes);
  const tasks = extractList(tasksRes);
  const documents: any[] = [];

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const activities: FeedActivityItem[] = React.useMemo(() => {
    const withDates = invoices.map((inv: any) => {
      const rawDate = inv.inv_date || inv.created_at || inv.updated_at || null;
      const dateObj = rawDate ? new Date(rawDate) : null;

      const people = inv.user?.people;
      const baseName = people
        ? `${people.first_name ?? ''} ${people.last_name ?? ''}`.trim()
        : (inv.user?.email as string | undefined) || 'User';

      const initials =
        (people &&
          ((people.first_name?.[0] ?? '') + (people.last_name?.[0] ?? '')).toUpperCase()) ||
        baseName
          .split(' ')
          .filter(Boolean)
          .map((p: string) => p[0])
          .join('')
          .toUpperCase();

      const user = {
        id: String(inv.user_id ?? ''),
        username: baseName,
        email: (inv.user?.email as string | undefined) || '',
        initials,
      };

      const timestamp =
        dateObj?.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }) ?? '';

      const item: FeedActivityItem & { _sortKey?: number } = {
        id: String(inv.id),
        user,
        action: 'created an invoice',
        target: (inv.invoice_no as string | undefined) || (inv.client?.name as string | undefined),
        timestamp,
        icon: 'fas fa-file-invoice',
        _sortKey: dateObj ? dateObj.getTime() : 0,
      };

      return item;
    });

    return withDates
      .sort((a, b) => (b._sortKey ?? 0) - (a._sortKey ?? 0))
      .slice(0, 6)
      .map(({ _sortKey, ...rest }) => rest);
  }, [invoices]);

  const monthlyRevenueBuckets = React.useMemo(() => {
    const buckets = Array.from({ length: 12 }, () => 0);
    invoices.forEach((inv: any) => {
      if (!inv.inv_date) return;
      const d = new Date(inv.inv_date);
      if (Number.isNaN(d.getTime())) return;
      if (d.getFullYear() !== currentYear) return;
      const idx = d.getMonth();
      const amount = Number(inv.total_amount ?? 0) || 0;
      buckets[idx] += amount;
    });
    return buckets;
  }, [invoices, currentYear]);

  const revenueData = React.useMemo(
    () => ({
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      datasets: [
        {
          label: 'Revenue (MWK)',
          data: monthlyRevenueBuckets,
          borderColor: palette.primaryLight,
          backgroundColor: withAlpha(palette.primaryLight, 0.15),
          tension: 0.4,
          fill: true,
        },
      ],
    }),
    [monthlyRevenueBuckets],
  );

  const activeCasesCount = React.useMemo(
    () =>
      cases.filter((c: any) => {
        const statusName = c.case_status?.case_status_name || c.status || '';
        return String(statusName).toLowerCase().includes('active');
      }).length,
    [cases],
  );

  const caseStatusBuckets = React.useMemo(() => {
    const buckets: Record<string, number> = {};
    cases.forEach((c: any) => {
      const raw = c.case_status?.case_status_name || c.status || 'Unknown';
      const key = String(raw);
      buckets[key] = (buckets[key] ?? 0) + 1;
    });
    return buckets;
  }, [cases]);

  const caseStatusLabels = Object.keys(caseStatusBuckets);
  const caseStatusValues = caseStatusLabels.map((l) => caseStatusBuckets[l]);

  const caseStatusData = React.useMemo(
    () => ({
      labels: caseStatusLabels,
      datasets: [
        {
          label: 'Cases',
          data: caseStatusValues,
          backgroundColor: caseStatusLabels.map((_, idx) => {
            const paletteColors = [
              withAlpha(palette.primary, 0.85),
              withAlpha(palette.warning, 0.85),
              withAlpha(palette.primaryLight, 0.85),
              withAlpha(palette.primaryDark, 0.85),
            ];
            return paletteColors[idx % paletteColors.length];
          }),
          borderColor: caseStatusLabels.map((_, idx) => {
            const paletteColors = [
              palette.primary,
              palette.warning,
              palette.primaryLight,
              palette.primaryDark,
            ];
            return paletteColors[idx % paletteColors.length];
          }),
          borderWidth: 1,
        },
      ],
    }),
    [caseStatusLabels, caseStatusValues],
  );

  const monthlyCasesData = React.useMemo(() => {
    const monthsBack = 6;
    const labels: string[] = [];
    const newCases: number[] = [];
    const closedCases: number[] = [];

    for (let i = monthsBack - 1; i >= 0; i -= 1) {
      const d = new Date(currentYear, currentMonth - i, 1);
      const label = d.toLocaleDateString(undefined, { month: 'short' });
      const year = d.getFullYear();
      const monthIndex = d.getMonth();

      labels.push(label);

      const newCount = cases.filter((c: any) => {
        const created = c.created_at ? new Date(c.created_at) : null;
        if (!created || Number.isNaN(created.getTime())) return false;
        return created.getFullYear() === year && created.getMonth() === monthIndex;
      }).length;

      const closedCount = cases.filter((c: any) => {
        const statusName = c.case_status?.case_status_name || c.status || '';
        const isClosed = String(statusName).toLowerCase().includes('closed');
        if (!isClosed) return false;
        const updated = c.updated_at ? new Date(c.updated_at) : null;
        if (!updated || Number.isNaN(updated.getTime())) return false;
        return updated.getFullYear() === year && updated.getMonth() === monthIndex;
      }).length;

      newCases.push(newCount);
      closedCases.push(closedCount);
    }

    return {
      labels,
      datasets: [
        {
          label: 'New Cases',
          data: newCases,
          backgroundColor: withAlpha(palette.primary, 0.85),
          borderColor: palette.primary,
          borderWidth: 1,
        },
        {
          label: 'Closed Cases',
          data: closedCases,
          backgroundColor: withAlpha(palette.primaryLight, 0.85),
          borderColor: palette.primaryLight,
          borderWidth: 1,
        },
      ],
    };
  }, [cases, currentYear, currentMonth]);

  const openTasksCount = React.useMemo(
    () =>
      tasks.filter((t: any) => {
        const status = t.project_status || '';
        return !String(status).toLowerCase().includes('completed') &&
          !String(status).toLowerCase().includes('closed');
      }).length,
    [tasks],
  );

  const documentsCount = documents.length;

  const currentMonthRevenue = React.useMemo(() => {
    return invoices.reduce((sum: number, inv: any) => {
      if (!inv.inv_date) return sum;
      const d = new Date(inv.inv_date);
      if (Number.isNaN(d.getTime())) return sum;
      if (d.getFullYear() !== currentYear || d.getMonth() !== currentMonth) return sum;
      const amount = Number(inv.total_amount ?? 0) || 0;
      return sum + amount;
    }, 0);
  }, [invoices, currentYear, currentMonth]);

  const quickStatsData = React.useMemo(
    () => [
      {
        title: 'Active Cases',
        value: activeCasesCount,
        icon: Folder as React.ComponentType<any>,
        color: 'primary' as const,
      },
      {
        title: 'Monthly Revenue',
        value: currentMonthRevenue
          ? `MWK ${currentMonthRevenue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : 'MWK 0.00',
        icon: DollarSign as React.ComponentType<any>,
        color: 'green' as const,
      },
      {
        title: 'Open Tasks',
        value: openTasksCount,
        icon: CheckSquare as React.ComponentType<any>,
        color: 'orange' as const,
      },
      {
        title: 'Documents',
        value: documentsCount,
        icon: FileText as React.ComponentType<any>,
        color: 'blue' as const,
      },
    ],
    [activeCasesCount, currentMonthRevenue, openTasksCount, documentsCount],
  );

  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">A quick overview of activity, revenue, and case trends.</p>

      <QuickStats stats={quickStatsData} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <ChartCard title="Revenue Overview">
          <LineChart data={revenueData} height={300} />
        </ChartCard>
        <ChartCard title="Case Status Distribution">
          <PieChart data={caseStatusData} height={300} />
        </ChartCard>
      </div>

      <div className="mb-6">
        <ChartCard title="Monthly Cases Comparison">
          <BarChart data={monthlyCasesData} height={300} />
        </ChartCard>
      </div>

      <div className="mb-6">
        <ActivityFeed activities={activities} maxItems={6} />
      </div>
    </div>
  );
};

export default DashboardPage;

